import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SlIcon } from '@shoelace-style/shoelace/dist/react';
import MediaThumb from './MediaThumb';
import StatusPill from './StatusPill';
import ThreadPanel from './ThreadPanel';
import VersionsList from './VersionsList';
import useCreativeThread from '../hooks/useCreativeThread';
import {
  canMessage,
  canPick,
  canUpload,
  formatEpoch,
  isPromotedSubType,
  isVideoSubType,
  subTypeLabel,
} from '../config';

/**
 * One segmented control, four views. Module level so the array identity is
 * stable and can never leak into a dependency list.
 */
const VIEWS = [
  { key: 'info', label: 'Info', icon: 'info' },
  { key: 'activity', label: 'Activity', icon: 'messages-square' },
  { key: 'versions', label: 'Versions', icon: 'history' },
  { key: 'media', label: 'Media', icon: 'image' },
];

const DEFAULT_VIEW = 'activity';

const MetaRow = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 py-1.5">
    <span className="shrink-0 text-xs font-medium text-slate-500">{label}</span>
    <span className="min-w-0 break-words text-right text-sm text-slate-800">{value || '—'}</span>
  </div>
);

/**
 * The current design. A video sub-type with both urls plays the video (using
 * the image as its poster) and still shows the thumbnail as its own tile, so
 * the designer can check both halves of the deliverable.
 */
const MediaPreview = ({ row, onZoom }) => {
  const [imgBroken, setImgBroken] = useState(false);
  const [videoBroken, setVideoBroken] = useState(false);
  const video = isVideoSubType(row?.sub_type);

  useEffect(() => {
    setImgBroken(false);
    setVideoBroken(false);
  }, [row?.img_urls, row?.video_urls]);

  const hasVideo = video && !!row?.video_urls && !videoBroken;
  const hasImage = !!row?.img_urls && !imgBroken;
  const stored = !!(row?.img_urls || row?.video_urls);

  if (!hasVideo && !hasImage) {
    return (
      <div className="flex h-40 flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 text-center">
        <SlIcon
          library="lucide"
          name={video ? 'video-off' : 'image-off'}
          className="mb-2 text-slate-300"
          style={{ fontSize: '24px' }}
        />
        <p className="text-sm font-medium text-slate-600">
          {stored ? 'Preview unavailable' : 'No design uploaded yet'}
        </p>
        <p className="mt-0.5 text-xs text-slate-400">
          {stored
            ? 'The stored file could not be loaded — try uploading it again.'
            : 'Upload a design to submit it for review.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {hasVideo ? (
        <div className="relative">
          <video
            src={row.video_urls}
            poster={hasImage ? row.img_urls : undefined}
            controls
            onError={() => setVideoBroken(true)}
            className="max-h-72 w-full rounded-xl border border-slate-200 bg-black"
          />
          <button
            type="button"
            onClick={() => onZoom(row.video_urls, true)}
            title="Open the video full screen"
            aria-label="Open the video full screen"
            className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-lg bg-black/55 text-white transition hover:bg-black/75"
          >
            <SlIcon library="lucide" name="maximize-2" style={{ fontSize: '14px' }} />
          </button>
        </div>
      ) : (
        <img
          src={row.img_urls}
          alt={row.creative_name || 'design'}
          onError={() => setImgBroken(true)}
          onClick={() => onZoom(row.img_urls, false)}
          className="max-h-72 w-full cursor-zoom-in rounded-xl border border-slate-200 bg-slate-50 object-contain"
        />
      )}

      {hasVideo && hasImage && (
        <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-2">
          <MediaThumb
            src={row.img_urls}
            alt="Thumbnail"
            className="h-12 w-12"
            onClick={() => onZoom(row.img_urls, false)}
          />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-slate-700">Thumbnail</p>
            <p className="text-[11px] text-slate-400">Shown before the video plays</p>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Right-side detail drawer (full screen below `sm`).
 *
 * Owns the thread hook for the selected creative; every action is delegated up
 * to `OrderBookingHome` so the list, the counts and the thread all update from
 * one response — no refetch loop, no polling.
 */
const DetailDrawer = ({
  open,
  row,
  onClose,
  onPick,
  onOpenUpload,
  onSendMessage,
  onSendAttachment,
  incoming,
  pickBusy,
}) => {
  const bscId = row?.bsc_id ?? null;
  const [view, setView] = useState(DEFAULT_VIEW);
  const [zoom, setZoom] = useState(null);

  const panelRef = useRef(null);
  const restoreFocusRef = useRef(null);
  const appliedNonceRef = useRef(null);

  const {
    detail,
    detailLoading,
    detailError,
    reloadDetail,
    logs,
    logsLoading,
    logsError,
    reloadLogs,
    versions,
    versionsLoading,
    versionsError,
    versionsLoaded,
    loadVersions,
    applyIncoming,
    applyDetailPatch,
    invalidateVersions,
  } = useCreativeThread(bscId, { enabled: open, seedRow: row });

  const current = detail || row;

  useEffect(() => {
    if (!open) return;
    setView(DEFAULT_VIEW);
    setZoom(null);
    appliedNonceRef.current = null;
  }, [open, bscId]);

  // Load versions lazily, the first time that view is opened. `versionsLoaded`
  // is set up-front by the hook, so a failed load never re-arms this into a
  // retry loop — the user retries from the inline button.
  useEffect(() => {
    if (open && view === 'versions' && !versionsLoaded && !versionsLoading) loadVersions();
  }, [open, view, versionsLoaded, versionsLoading, loadVersions]);

  // The single seam for externally produced activity (action responses today,
  // a socket subscription later). Keyed on a primitive nonce.
  useEffect(() => {
    if (!incoming || !bscId) return;
    if (Number(incoming.bsc_id) !== Number(bscId)) return;
    if (appliedNonceRef.current === incoming.nonce) return;
    appliedNonceRef.current = incoming.nonce;
    if (incoming.logs?.length) applyIncoming(incoming.logs);
    if (incoming.patch) applyDetailPatch(incoming.patch);
    if (incoming.invalidateVersions) invalidateVersions();
  }, [incoming, bscId, applyIncoming, applyDetailPatch, invalidateVersions]);

  // Scroll lock + focus capture/restore.
  useEffect(() => {
    if (!open) return undefined;
    restoreFocusRef.current = document.activeElement;
    const body = document.body;
    const outlet = document.querySelector('.outlet-body');
    const prevBody = body.style.overflow;
    const prevOutlet = outlet ? outlet.style.overflow : '';
    body.style.overflow = 'hidden';
    if (outlet) outlet.style.overflow = 'hidden';
    const timer = setTimeout(() => panelRef.current?.focus(), 0);
    return () => {
      clearTimeout(timer);
      body.style.overflow = prevBody;
      if (outlet) outlet.style.overflow = prevOutlet;
      const el = restoreFocusRef.current;
      if (el && typeof el.focus === 'function') el.focus();
    };
  }, [open]);

  const closeZoom = useCallback(() => setZoom(null), []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (zoom) {
        e.stopPropagation();
        closeZoom();
        return;
      }
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, zoom, closeZoom, onClose]);

  const handleZoom = useCallback((url, isVideo) => setZoom({ url, isVideo }), []);

  const handleSend = useCallback(
    async (text) => onSendMessage(bscId, text),
    [onSendMessage, bscId]
  );

  const handleAttach = useCallback(
    (file, caption, onUploadProgress) => onSendAttachment(bscId, file, caption, onUploadProgress),
    [onSendAttachment, bscId]
  );

  // Counts are shown only when the number is genuinely known — never a fake 0.
  const messageCount = useMemo(
    () => logs.reduce((n, log) => (log?.activity_type === 'message' ? n + 1 : n), 0),
    [logs]
  );

  if (!open || !current) return null;

  const promoted = isPromotedSubType(current.sub_type);
  const status = Number(current.status);
  const showPick = canPick(status);
  const showUpload = canUpload(status);
  const hasDesignFile = !!(current.img_urls || current.video_urls);
  const versionCount = versionsLoaded && !versionsLoading ? versions.length : 0;

  const badgeFor = (key) => {
    if (key === 'activity') return messageCount > 0 ? String(messageCount) : null;
    if (key === 'versions') return versionCount > 0 ? String(versionCount) : null;
    return null;
  };

  const uploadButton = (
    <button
      type="button"
      onClick={() => onOpenUpload(current)}
      className="inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-700"
    >
      <SlIcon library="lucide" name="upload" style={{ fontSize: '14px' }} />
      Upload design
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-slate-900/40 backdrop-blur-[2px]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Creative ${current.creative_name || current.bsc_id}`}
        tabIndex={-1}
        className="flex h-full w-full flex-col bg-white shadow-2xl outline-none sm:max-w-xl"
      >
        {/* Header */}
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {/* NOT `truncate`: Styles.scss:3997 adds `max-width: 130px` to
                  that class. `min-w-0` is required here because the heading is
                  a flex item — without it the title refuses to shrink. */}
              <h2 className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-base font-semibold text-slate-900">
                {current.creative_name || `Creative #${current.bsc_id}`}
              </h2>
              <StatusPill status={current.status} />
            </div>
            <p className="mt-0.5 text-xs text-slate-500">
              #{current.bsc_id} · {subTypeLabel(current.sub_type)}
              {detailLoading ? ' · refreshing…' : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close details"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          >
            <SlIcon library="lucide" name="x" style={{ fontSize: '18px' }} />
          </button>
        </header>

        {detailError && (
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
            <span>{detailError}</span>
            <button
              type="button"
              onClick={reloadDetail}
              className="rounded border border-amber-300 bg-white px-2 py-1 font-semibold text-amber-800"
            >
              Retry
            </button>
          </div>
        )}

        {/* One segmented control for the whole drawer. */}
        <div
          role="tablist"
          aria-label="Creative views"
          className="flex shrink-0 gap-1 overflow-x-auto border-b border-slate-200 bg-slate-50 px-2 py-2"
        >
          {VIEWS.map((v) => {
            const active = view === v.key;
            const badge = badgeFor(v.key);
            return (
              <button
                key={v.key}
                type="button"
                role="tab"
                id={`ob-drawer-tab-${v.key}`}
                aria-selected={active}
                aria-controls={`ob-drawer-panel-${v.key}`}
                onClick={() => setView(v.key)}
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium transition ${
                  active
                    ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <SlIcon library="lucide" name={v.icon} style={{ fontSize: '14px' }} />
                {v.label}
                {badge && (
                  <span
                    className={`rounded-full px-1.5 text-[10px] font-semibold ${
                      active ? 'bg-sky-100 text-sky-700' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {badge}
                  </span>
                )}
                {v.key === 'media' && hasDesignFile && (
                  <span
                    aria-hidden="true"
                    className="h-1.5 w-1.5 rounded-full bg-emerald-500"
                    title="A design file is attached"
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Action bar — reachable from every view, never inside the scroller. */}
        {(showPick || showUpload) && (
          <div className="flex shrink-0 flex-wrap gap-2 border-b border-slate-200 px-4 py-2.5">
            {showPick && (
              <button
                type="button"
                disabled={pickBusy}
                onClick={() => onPick(current)}
                className="inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {pickBusy ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-sky-300 border-t-white" />
                ) : (
                  <SlIcon library="lucide" name="hand" style={{ fontSize: '14px' }} />
                )}
                {pickBusy ? 'Picking…' : 'Pick this design'}
              </button>
            )}
            {showUpload && uploadButton}
          </div>
        )}

        <div
          role="tabpanel"
          id={`ob-drawer-panel-${view}`}
          aria-labelledby={`ob-drawer-tab-${view}`}
          className="min-h-0 flex-1"
        >
          {view === 'info' && (
            <div className="h-full space-y-3 overflow-y-auto px-4 py-3">
              <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 px-3">
                <MetaRow
                  label="Sales person"
                  value={
                    current.sales_person_name
                      ? `${current.sales_person_name}${current.sales_person_id ? ` (#${current.sales_person_id})` : ''}`
                      : current.sales_person_id
                        ? `#${current.sales_person_id}`
                        : '—'
                  }
                />
                <MetaRow label="Created" value={formatEpoch(current.created_time)} />
                <MetaRow label="Creative type" value={current.creative_type} />
                <div className="flex items-center justify-between gap-4 py-2">
                  <span className="shrink-0 text-xs font-medium text-slate-500">Current status</span>
                  <StatusPill status={current.status} size="md" />
                </div>
              </div>

              {promoted && (
                <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Story content
                  </p>
                  <div className="divide-y divide-slate-200">
                    <MetaRow label="Headline" value={current.head_line} />
                    <MetaRow label="Description" value={current.description} />
                    <MetaRow label="Link text" value={current.text_in_link} />
                  </div>
                </div>
              )}
            </div>
          )}

          {view === 'activity' && (
            <ThreadPanel
              logs={logs}
              loading={logsLoading}
              error={logsError}
              onRetry={reloadLogs}
              canSend={canMessage(status)}
              // Two separate gates, on purpose: messaging follows `canMessage`
              // (2/3/4/5), the composer's design button follows `canUpload`
              // (3/4/5) — the same flag the action bar above uses. There is no
              // second modal: this reuses the one instance in OrderBookingHome.
              canUploadDesign={showUpload}
              lockedReason={
                status >= 6
                  ? 'This creative is approved — the conversation is closed.'
                  : 'This conversation is closed.'
              }
              onSend={handleSend}
              onSendAttachment={handleAttach}
              onOpenUpload={() => onOpenUpload(current)}
              onZoom={handleZoom}
            />
          )}

          {view === 'versions' && (
            <div className="h-full overflow-y-auto bg-slate-50">
              <VersionsList
                versions={versions}
                loading={versionsLoading}
                error={versionsError}
                onRetry={loadVersions}
                onZoom={handleZoom}
              />
            </div>
          )}

          {view === 'media' && (
            <div className="h-full space-y-3 overflow-y-auto px-4 py-3">
              <MediaPreview row={current} onZoom={handleZoom} />

              {showUpload && <div className="flex flex-wrap gap-2">{uploadButton}</div>}

              <p className="flex items-start gap-1.5 text-xs text-slate-500">
                <SlIcon
                  library="lucide"
                  name="info"
                  className="mt-[1px] shrink-0"
                  style={{ fontSize: '13px' }}
                />
                <span>
                  Uploading a design replaces the current file and submits it for review. To share a
                  reference, attach it in Activity instead.
                </span>
              </p>
            </div>
          )}
        </div>
      </aside>

      {zoom && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeZoom();
          }}
        >
          <button
            type="button"
            onClick={closeZoom}
            aria-label="Close preview"
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
          >
            <SlIcon library="lucide" name="x" style={{ fontSize: '20px' }} />
          </button>
          {zoom.isVideo ? (
            <video src={zoom.url} controls autoPlay className="max-h-[88vh] max-w-full rounded-lg" />
          ) : (
            <img
              src={zoom.url}
              alt="preview"
              className="max-h-[88vh] max-w-full rounded-lg object-contain"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default DetailDrawer;
